import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import TeamLogoUpload from "@/components/workspace/TeamLogoUpload";

import useWorkspaceSettings from "@/hooks/useWorkspaceSettings";
import { supabase } from "@/lib/supabase";
import useAuthStore from "@/store/authStore";
import { AnimatePresence, motion } from "framer-motion";
import {
  Crown,
  Edit2,
  Key,
  Search,
  Settings,
  UserPlus,
  UserRound,
  Users,
  Users2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Add Permission Management Dialog Component
function PermissionManagementDialog({
  isOpen,
  onClose,
  member,
  onUpdatePermissions,
  isUpdating,
}) {
  const [permissions, setPermissions] = useState(member?.permissions || {});

  // Reset permissions when member changes
  useEffect(() => {
    if (member?.permissions) {
      setPermissions(member.permissions);
    }
  }, [member]);

  const handlePermissionChange = (category, permission, value) => {
    setPermissions((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: value,
      },
    }));
  };

  const handleSave = () => {
    onUpdatePermissions(member.user_id, permissions);
  };

  const permissionCategories = [
    {
      key: "files",
      title: "Files",
      description: "Manage file operations in the workspace",
      permissions: [
        {
          key: "upload",
          label: "Upload Files",
          description: "Can upload files to the workspace",
        },
        {
          key: "delete",
          label: "Delete Files",
          description: "Can delete files from the workspace",
        },
        {
          key: "share",
          label: "Share Files",
          description: "Can share files with others",
        },
      ],
    },
    {
      key: "memories",
      title: "Memories",
      description: "Manage workspace memories and knowledge base",
      permissions: [
        {
          key: "create",
          label: "Create Memories",
          description: "Can create new memories",
        },
        {
          key: "edit",
          label: "Edit Memories",
          description: "Can edit existing memories",
        },
        {
          key: "delete",
          label: "Delete Memories",
          description: "Can delete memories",
        },
      ],
    },
    {
      key: "settings",
      title: "Settings",
      description: "Access workspace settings and configuration",
      permissions: [
        {
          key: "view",
          label: "View Settings",
          description: "Can view workspace settings",
        },
        {
          key: "edit",
          label: "Edit Settings",
          description: "Can modify workspace settings",
        },
      ],
    },
    {
      key: "workspace",
      title: "Workspace Management",
      description: "Administrative workspace permissions",
      permissions: [
        {
          key: "edit",
          label: "Edit Workspace",
          description: "Can edit workspace details",
        },
        {
          key: "delete",
          label: "Delete Workspace",
          description: "Can delete the workspace",
        },
        {
          key: "manage_roles",
          label: "Manage Roles",
          description: "Can change member roles and permissions",
        },
        {
          key: "invite_members",
          label: "Invite Members",
          description: "Can invite new members to the workspace",
        },
      ],
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl rounded-2xl bg-white dark:bg-zinc-900 border-gray-100 dark:border-zinc-800 shadow-xl"
        style={{ zIndex: 10000 }}
      >
        <Button
          variant="outline"
          size="icon"
          onClick={onClose}
          className="absolute right-4 top-4 h-8 w-8 rounded-full text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 z-[450]"
        >
          <X className="h-5 w-5" />
        </Button>

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-purple-500" />
            Manage Permissions
          </DialogTitle>
          <DialogDescription>
            Configure permissions for{" "}
            {member?.full_name || member?.display_name || "this member"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
          {permissionCategories.map((category) => (
            <div key={category.key} className="space-y-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  {category.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {category.description}
                </p>
              </div>

              <div className="space-y-2">
                {category.permissions.map((permission) => (
                  <div
                    key={permission.key}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg"
                  >
                    <div className="flex-1">
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {permission.label}
                      </Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {permission.description}
                      </p>
                    </div>
                    <Switch
                      checked={
                        permissions[category.key]?.[permission.key] || false
                      }
                      onCheckedChange={(checked) =>
                        handlePermissionChange(
                          category.key,
                          permission.key,
                          checked
                        )
                      }
                      disabled={isUpdating}
                      className="data-[state=checked]:bg-purple-600"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isUpdating}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {isUpdating ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Updating...
              </>
            ) : (
              "Save Permissions"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkspaceSettingsDialog({
  isOpen,
  onOpenChange,
  workspace,
  onSaveChanges,
  onDeleteWorkspace,
  isLoading,
}) {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Add permission management state
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] =
    useState(null);
  const [isUpdatingPermissions, setIsUpdatingPermissions] = useState(false);

  // Use the workspace settings hook
  const {
    // Form state
    editForm,
    setEditForm,
    workspaceSettings,
    setWorkspaceSettings,
    logoData,
    setLogoData,

    // UI state
    activeMainTab,
    setActiveMainTab,
    activeTeamTab,
    setActiveTeamTab,
    showSuccessNotification,
    setShowSuccessNotification,
    successMessage,
    setSuccessMessage,

    // Team management state
    searchQuery,
    setSearchQuery,
    isSearching,
    selectedUsers,
    setSelectedUsers,
    inviteMessage,
    setInviteMessage,
    isSending,
    members,
    pendingInvites,
    sentInvitations,
    recentlySentInvites,
    isLoadingStatus,
    searchResults,
    currentPage,
    hasMoreResults,
    loadingMoreUsers,
    realtimeChannel,
    resultsContainerRef,

    // Role management state
    workspaceMembers,
    setWorkspaceMembers,
    isLoadingMembers,
    userRole,
    isOwner,

    // Transfer ownership state
    showTransferDialog,
    setShowTransferDialog,
    selectedNewOwner,
    setSelectedNewOwner,
    isTransferring,

    // Utility functions
    getInitials,
    getAvatarUrl,
    getUserDisplayName,
    getRoleBadge,
    refreshWorkspaceStatus,

    // Handler functions
    handleSettingChange,
    handleLogoChange,
    handleSubmit,
    handleDeleteWorkspace,
    handleUserSearch,
    handleSelectUser,
    handleRemoveUser,
    handleSendInvitations,
    handleRoleChange,
    handleRemoveMember,
    handleTransferOwnership,
  } = useWorkspaceSettings({
    workspace,
    isOpen,
    user,
    onSaveChanges,
    onDeleteWorkspace,
  });

  // Add permission management functions
  const handleManagePermissions = (member) => {
    setSelectedMemberForPermissions(member);
    setPermissionDialogOpen(true);
  };

  const handleUpdatePermissions = async (userId, newPermissions) => {
    if (!workspace?.id || !isOwner) return;

    setIsUpdatingPermissions(true);
    try {
      // Update permissions in the database
      const { error } = await supabase
        .from("workspace_members")
        .update({
          permissions: newPermissions,
        })
        .eq("workspace_id", workspace.id)
        .eq("user_id", userId);

      if (error) throw error;

      // Update local state
      setWorkspaceMembers((prev) =>
        prev.map((member) =>
          member.user_id === userId
            ? { ...member, permissions: newPermissions }
            : member
        )
      );

      // Show success message
      setSuccessMessage("Permissions updated successfully!");
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);

      // Close dialog
      setPermissionDialogOpen(false);
      setSelectedMemberForPermissions(null);
    } catch (error) {
      console.error("Error updating permissions:", error);
      // You might want to show an error message here
    } finally {
      setIsUpdatingPermissions(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className={`max-w-[90vw] max-h-[90vh] w-[800px] ${workspace?.is_team ? "h-[600px]" : "h-[350px]"} bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden`}
          style={{ zIndex: 9999 }}
          forceMount
          hideCloseButton={true}
        >
          <DialogTitle className="sr-only">Project Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Manage project details, preferences and options
          </DialogDescription>

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Project settings
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8 bg-transparent hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-900 dark:text-white border-none"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {workspace?.is_team ? (
            <div className="flex flex-1 overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
              <Tabs
                value={activeMainTab}
                onValueChange={setActiveMainTab}
                className="h-full flex w-full"
              >
                {/* Sidebar */}
                <div className="w-40 border-r border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 flex-col">
                  <TabsList className="flex flex-col w-full !bg-transparent p-3 space-y-1 flex-1 justify-start">
                    <TabsTrigger
                      value="details"
                      className="flex items-center gap-2 rounded-2xl px-2 py-2 w-full justify-start text-sm font-medium data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors data-[state=active]:shadow-none !bg-transparent hover:!bg-gray-100 dark:hover:!bg-zinc-800/50 data-[state=active]:!bg-transparent min-h-[36px]"
                    >
                      <Edit2 className="h-4 w-4" />
                      <span>Details</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="team"
                      className="flex items-center gap-2 rounded-2xl px-2 py-2 w-full justify-start text-sm font-medium data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors data-[state=active]:shadow-none !bg-transparent hover:!bg-gray-100 dark:hover:!bg-zinc-800/50 data-[state=active]:!bg-transparent min-h-[36px] relative"
                    >
                      <Users className="h-4 w-4" />
                      <span>Team</span>
                      {(members.length > 0 ||
                        pendingInvites.length > 0 ||
                        sentInvitations.length > 0) && (
                        <span className="ml-auto bg-purple-500 text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">
                          {members.length +
                            pendingInvites.length +
                            sentInvitations.length}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="settings"
                      className="flex items-center gap-2 rounded-2xl px-2 py-2 w-full justify-start text-sm font-medium data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors data-[state=active]:shadow-none !bg-transparent hover:!bg-gray-100 dark:hover:!bg-zinc-800/50 data-[state=active]:!bg-transparent min-h-[36px]"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Tab Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 md:rounded-tr-2xl md:rounded-br-2xl">
                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-auto">
                    {/* Details Tab */}
                    <TabsContent
                      value="details"
                      className="h-full m-0 data-[state=active]:block"
                    >
                      <ScrollArea className="h-full w-full">
                        <div className="p-4 md:p-6 pb-20">
                          <div className="space-y-4">
                            <h3 className="text-base font-medium text-gray-800 dark:text-zinc-200 flex items-center gap-2 mb-3">
                              <Edit2 className="h-4 w-4 text-purple-500" />
                              Project Details
                            </h3>
                            <div className="space-y-3">
                              <div className="space-y-1.5">
                                <Label
                                  htmlFor="name"
                                  className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                                >
                                  Name
                                </Label>
                                <Input
                                  id="name"
                                  value={editForm.name}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      name: e.target.value,
                                    })
                                  }
                                  className="border-gray-200 dark:border-zinc-700 focus:border-purple-400/50 bg-gray-50 dark:bg-zinc-800/50 rounded-lg h-9"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  htmlFor="description"
                                  className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                                >
                                  Description
                                </Label>
                                <Textarea
                                  id="description"
                                  value={editForm.description}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      description: e.target.value,
                                    })
                                  }
                                  className="border-gray-200 dark:border-zinc-700 focus:border-purple-400/50 bg-gray-50 dark:bg-zinc-800/50 rounded-lg min-h-[60px] resize-none"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  htmlFor="instructions"
                                  className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                                >
                                  Instructions
                                </Label>
                                <Textarea
                                  id="instructions"
                                  value={editForm.instructions}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      instructions: e.target.value,
                                    })
                                  }
                                  className="border-gray-200 dark:border-zinc-700 focus:border-purple-400/50 bg-gray-50 dark:bg-zinc-800/50 rounded-lg min-h-[60px] resize-none"
                                />
                              </div>
                              {workspace?.is_team && (
                                <div className="space-y-1.5">
                                  <TeamLogoUpload
                                    onImageChange={handleLogoChange}
                                    existingImagePath={workspace?.image_path}
                                    workspaceId={workspace?.id}
                                    key={`logo-upload-${workspace?.id}-${workspace?.image_path}`}
                                  />
                                </div>
                              )}
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
                                  <div className="space-y-0.5">
                                    <Label
                                      htmlFor="include_workspace_instructions"
                                      className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                                    >
                                      Include Workspace Instructions
                                    </Label>
                                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                                      Include workspace instructions in AI
                                      conversations
                                    </p>
                                  </div>
                                  <Switch
                                    id="include_workspace_instructions"
                                    checked={
                                      workspaceSettings.include_workspace_instructions
                                    }
                                    onCheckedChange={(checked) =>
                                      handleSettingChange(
                                        "include_workspace_instructions",
                                        checked
                                      )
                                    }
                                    disabled={isLoading}
                                    className="data-[state=checked]:bg-purple-600"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    {/* Team Tab */}
                    <TabsContent
                      value="team"
                      className="h-full m-0 data-[state=active]:block"
                    >
                      <ScrollArea className="h-full w-full">
                        <div className="p-4 md:p-6 pb-20">
                          <div className="space-y-4">
                            <h3 className="text-base font-medium text-gray-800 dark:text-zinc-200 flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-purple-500" />
                              Team Management
                            </h3>

                            <Tabs
                              value={activeTeamTab}
                              onValueChange={setActiveTeamTab}
                              className="w-full"
                            >
                              <TabsList className="grid w-full grid-cols-2 gap-1 bg-gray-100 dark:bg-zinc-800/80 p-1 rounded-md">
                                <TabsTrigger
                                  value="invites"
                                  className="rounded-md bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 text-gray-700 dark:text-gray-300 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm relative"
                                >
                                  Invitations
                                  {(pendingInvites.length > 0 ||
                                    sentInvitations.length > 0) && (
                                    <span className="ml-1.5 bg-purple-500 text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">
                                      {pendingInvites.length +
                                        sentInvitations.length}
                                    </span>
                                  )}
                                </TabsTrigger>
                                <TabsTrigger
                                  value="members"
                                  className="rounded-md bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 text-gray-700 dark:text-gray-300 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                                >
                                  Members
                                  {workspaceMembers.length > 0 && (
                                    <span className="ml-1.5 bg-purple-500 text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">
                                      {workspaceMembers.length}
                                    </span>
                                  )}
                                </TabsTrigger>
                              </TabsList>

                              <div className="mt-4">
                                <TabsContent value="invites" className="m-0">
                                  <div className="space-y-4">
                                    {/* Search and invite section */}
                                    <div className="flex items-center space-x-2">
                                      <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                          placeholder="Search users by name or email"
                                          className="pl-10 border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus-visible:ring-purple-400"
                                          value={searchQuery}
                                          onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              handleUserSearch();
                                            }
                                          }}
                                        />
                                      </div>
                                      <Button
                                        onClick={handleUserSearch}
                                        disabled={
                                          isSearching || !searchQuery.trim()
                                        }
                                        className="bg-purple-600 hover:bg-purple-700 text-white"
                                      >
                                        {isSearching ? (
                                          <Spinner className="h-4 w-4 text-white" />
                                        ) : (
                                          "Search"
                                        )}
                                      </Button>
                                    </div>

                                    {/* Selected users */}
                                    {selectedUsers.length > 0 && (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Selected users
                                          </h4>
                                          <Badge
                                            variant="outline"
                                            className="bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-zinc-700 px-2 py-0.5 text-xs font-medium"
                                          >
                                            {selectedUsers.length}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {selectedUsers.map((user) => (
                                            <Badge
                                              key={user.id}
                                              variant="secondary"
                                              className="py-1.5 pl-1.5 pr-2 flex items-center gap-1.5 bg-white hover:bg-purple-500 dark:bg-zinc-800 dark:hover:bg-purple-800/50 text-gray-700 hover:text-white dark:text-gray-200 dark:hover:text-white border border-gray-100 hover:border-purple-400 dark:border-zinc-700 dark:hover:border-purple-700 shadow-sm transition-colors"
                                            >
                                              <Avatar className="h-5 w-5 shrink-0">
                                                <AvatarImage
                                                  src={getAvatarUrl(user)}
                                                />
                                                <AvatarFallback className="text-[10px] bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300">
                                                  {getInitials(
                                                    user.full_name ||
                                                      user.display_name
                                                  )}
                                                </AvatarFallback>
                                              </Avatar>
                                              <span className="text-xs truncate max-w-[150px]">
                                                {getUserDisplayName(user)}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveUser(user.id);
                                                }}
                                                className="ml-1 bg-transparent text-red-500 hover:text-white dark:text-red-400 dark:hover:text-white p-0.5 rounded-full z-10"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Search results */}
                                    <AnimatePresence>
                                      {(isSearching ||
                                        searchResults.length > 0 ||
                                        (searchQuery && !isSearching)) && (
                                        <motion.div
                                          ref={resultsContainerRef}
                                          initial={{
                                            opacity: 0,
                                            y: -10,
                                            height: 0,
                                          }}
                                          animate={{
                                            opacity: 1,
                                            y: 0,
                                            height: "auto",
                                          }}
                                          exit={{
                                            opacity: 0,
                                            y: -10,
                                            height: 0,
                                          }}
                                          transition={{
                                            duration: 0.3,
                                            ease: "easeOut",
                                          }}
                                          className="border border-gray-200 dark:border-zinc-700 rounded-md mt-2 max-h-[200px] overflow-y-auto"
                                        >
                                          {isSearching &&
                                          searchResults.length === 0 ? (
                                            <div className="p-4 text-center">
                                              <Spinner className="h-5 w-5 mx-auto text-purple-500" />
                                              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                Searching for users...
                                              </p>
                                            </div>
                                          ) : searchResults.length > 0 ? (
                                            <>
                                              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                                                {searchResults.map((user) => (
                                                  <li
                                                    key={user.id}
                                                    className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50 cursor-pointer"
                                                    onClick={() =>
                                                      handleSelectUser(user)
                                                    }
                                                  >
                                                    <div className="flex items-center gap-3">
                                                      <Avatar className="h-8 w-8">
                                                        <AvatarImage
                                                          src={getAvatarUrl(
                                                            user
                                                          )}
                                                        />
                                                        <AvatarFallback>
                                                          {getInitials(
                                                            user.full_name ||
                                                              user.display_name
                                                          )}
                                                        </AvatarFallback>
                                                      </Avatar>
                                                      <div>
                                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                          {getUserDisplayName(
                                                            user
                                                          )}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                          {user.email}
                                                        </p>
                                                      </div>
                                                    </div>
                                                    <UserPlus className="h-4 w-4 text-gray-400" />
                                                  </li>
                                                ))}
                                              </ul>
                                            </>
                                          ) : searchQuery ? (
                                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                              <Users2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                              <p>
                                                No users found matching &quot;
                                                {searchQuery}&quot;
                                              </p>
                                            </div>
                                          ) : null}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>

                                    {/* Invitation message */}
                                    <AnimatePresence>
                                      {selectedUsers.length > 0 && (
                                        <motion.div
                                          initial={{
                                            opacity: 0,
                                            y: -10,
                                            height: 0,
                                          }}
                                          animate={{
                                            opacity: 1,
                                            y: 0,
                                            height: "auto",
                                          }}
                                          exit={{
                                            opacity: 0,
                                            y: -10,
                                            height: 0,
                                          }}
                                          transition={{
                                            duration: 0.3,
                                            ease: "easeOut",
                                          }}
                                          className="space-y-2 pt-3"
                                        >
                                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Invitation message
                                          </h4>
                                          <Textarea
                                            placeholder="Add a personal message to your invitation"
                                            value={inviteMessage}
                                            onChange={(e) =>
                                              setInviteMessage(e.target.value)
                                            }
                                            className="min-h-24 border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus-visible:ring-purple-400"
                                          />
                                          <Button
                                            onClick={handleSendInvitations}
                                            disabled={
                                              selectedUsers.length === 0 ||
                                              isSending
                                            }
                                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                          >
                                            {isSending ? (
                                              <Spinner className="h-4 w-4 mr-2" />
                                            ) : null}
                                            {isSending
                                              ? "Sending..."
                                              : "Send Invitations"}
                                          </Button>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                </TabsContent>

                                <TabsContent value="members" className="m-0">
                                  {isLoadingMembers ? (
                                    <div className="text-center py-6">
                                      <Spinner className="h-6 w-6 mx-auto text-purple-500" />
                                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                        Loading members...
                                      </p>
                                    </div>
                                  ) : workspaceMembers.length === 0 ? (
                                    <div className="text-center py-6 space-y-2">
                                      <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
                                        <UserRound className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                      </div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        No members yet
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      <ul className="divide-y divide-gray-100 dark:divide-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-md">
                                        {workspaceMembers.map((member) => (
                                          <li
                                            key={member.user_id}
                                            className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors"
                                          >
                                            <div className="flex items-center gap-3">
                                              <Avatar className="h-8 w-8">
                                                <AvatarImage
                                                  src={getAvatarUrl(member)}
                                                />
                                                <AvatarFallback className="text-xs">
                                                  {getInitials(
                                                    member.full_name ||
                                                      member.display_name
                                                  )}
                                                </AvatarFallback>
                                              </Avatar>
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                    {getUserDisplayName(member)}
                                                  </p>
                                                  {member.user_id ===
                                                    user?.id && (
                                                    <Badge
                                                      variant="outline"
                                                      className="text-purple-600 border-purple-200 bg-purple-50 dark:text-purple-400 dark:border-purple-800/30 dark:bg-purple-900/20 text-[10px] px-1.5 py-0.5"
                                                    >
                                                      You
                                                    </Badge>
                                                  )}
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  {member.email}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              {getRoleBadge(member.role)}
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </TabsContent>
                              </div>
                            </Tabs>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    {/* Settings Tab */}
                    <TabsContent
                      value="settings"
                      className="h-full m-0 data-[state=active]:block"
                    >
                      <ScrollArea className="h-full w-full">
                        <div className="p-4 md:p-6 pb-20">
                          <div className="space-y-4">
                            <h3 className="text-base font-medium text-gray-800 dark:text-zinc-200 flex items-center gap-2 mb-3">
                              <Settings className="h-4 w-4 text-purple-500" />
                              General Settings
                            </h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
                                <div className="space-y-0.5">
                                  <Label
                                    htmlFor="notifications"
                                    className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                                  >
                                    Notifications
                                  </Label>
                                  <p className="text-xs text-gray-500 dark:text-zinc-400">
                                    Receive alerts about project activity
                                  </p>
                                </div>
                                <Switch
                                  id="notifications"
                                  checked={workspaceSettings.notifications}
                                  onCheckedChange={(checked) =>
                                    handleSettingChange(
                                      "notifications",
                                      checked
                                    )
                                  }
                                  disabled={isLoading || !isOwner}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          ) : (
            // Personal workspace - simple settings form
            <div className="flex-1 overflow-auto bg-white dark:bg-zinc-900 rounded-b-2xl">
              <div className="p-4 md:p-6 pb-20">
                <div className="w-full space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="name"
                        className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                      >
                        Name
                      </Label>
                      <Input
                        id="name"
                        value={editForm.name}
                        onChange={(e) =>
                          setEditForm({ ...editForm, name: e.target.value })
                        }
                        className="border-gray-200 dark:border-zinc-700 focus:border-purple-400/50 bg-gray-50 dark:bg-zinc-800/50 rounded-lg h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="description"
                        className="text-sm font-medium text-gray-700 dark:text-zinc-300"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="description"
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            description: e.target.value,
                          })
                        }
                        className="border-gray-200 dark:border-zinc-700 focus:border-purple-400/50 bg-gray-50 dark:bg-zinc-800/50 rounded-lg min-h-[60px] resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-2 justify-end p-4 border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-b-2xl">
            <Button
              variant="outline"
              onClick={handleDeleteWorkspace}
              disabled={isLoading}
              size="sm"
              className="bg-transparent border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl flex items-center gap-1.5"
            >
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="outline"
              onClick={handleSubmit}
              disabled={isLoading || !editForm.name}
              size="sm"
              className="bg-transparent border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-2xl"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>

        {/* Transfer Ownership Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent className="max-w-md" hideCloseButton={true}>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                setShowTransferDialog(false);
                setSelectedNewOwner(null);
              }}
              className="absolute right-4 top-4 h-8 w-8 rounded-full text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 z-[450]"
            >
              <X className="h-5 w-5" />
            </Button>

            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Transfer Ownership
              </DialogTitle>
              <DialogDescription>
                Select a member to transfer ownership to. You will become a
                regular member after the transfer.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select New Owner</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {workspaceMembers
                    .filter(
                      (member) =>
                        member.user_id !== user?.id && member.role !== "owner"
                    )
                    .map((member) => (
                      <div
                        key={member.user_id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedNewOwner?.user_id === member.user_id
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                            : "border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                        }`}
                        onClick={() => setSelectedNewOwner(member)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={getAvatarUrl(member)} />
                            <AvatarFallback className="text-xs">
                              {getInitials(
                                member.full_name || member.display_name
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {getUserDisplayName(member)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.email}
                            </p>
                          </div>
                          {selectedNewOwner?.user_id === member.user_id && (
                            <div className="h-4 w-4 rounded-full bg-purple-500 flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>

                {workspaceMembers.filter(
                  (member) =>
                    member.user_id !== user?.id && member.role !== "owner"
                ).length === 0 && (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                    <UserRound className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      No other members available for ownership transfer
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={handleTransferOwnership}
                disabled={!selectedNewOwner || isTransferring}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {isTransferring ? (
                  <>
                    <Spinner className="h-4 w-4 mr-2" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <Crown className="h-4 w-4 mr-2" />
                    Transfer Ownership
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Dialog>

      {/* Permission Management Dialog */}
      <PermissionManagementDialog
        isOpen={permissionDialogOpen}
        onClose={() => {
          setPermissionDialogOpen(false);
          setSelectedMemberForPermissions(null);
        }}
        member={selectedMemberForPermissions}
        onUpdatePermissions={handleUpdatePermissions}
        isUpdating={isUpdatingPermissions}
      />
    </>
  );
}
